import { Fragment, useEffect, useMemo, useState, type ReactNode } from "react";
import { ChevronDown, ChevronRight, ChevronUp, ChevronsUpDown, Search } from "lucide-react";
import { Table, THead, TBody, TR, TH, TD, Input } from "@/design-system/components/ui";
import { cn } from "@/design-system/lib/utils";

export interface Column<T> {
  key: string;
  header: string;
  render: (row: T) => ReactNode;
  className?: string;
  /**
   * Opt-in: hide this column below the sm breakpoint (phones). Default
   * false — staff surfaces render every column exactly as before. Enable
   * only on columns whose loss keeps the row's identity, status, and
   * primary action readable at 375px.
   */
  mobileHidden?: boolean;
  /** Enables click-to-sort on this column header when DataTable.sortable is true. */
  sortable?: boolean;
  /** Returns the value used for sorting this column. Required when sortable. */
  sortAccessor?: (row: T) => string | number | Date | null | undefined;
}

type SortState = { key: string; dir: "asc" | "desc" } | null;

interface DataTableProps<T> {
  columns: Column<T>[];
  rows: T[];
  loading?: boolean;
  error?: string | null;
  emptyMessage?: string;
  rowKey: (row: T) => string;
  /**
   * Selection support (UX Enhancements §12). When <code>selectedIds</code>
   * is provided, a checkbox column is rendered and the host page owns the
   * selection state. Pair with {@link BulkActionBar} from
   * <code>@/components/ux</code> to act on the selection.
   */
  selectedIds?: string[];
  onSelectionChange?: (next: string[]) => void;
  /**
   * Opt-in per-row selectability. Rows for which this returns false render a
   * disabled checkbox and are excluded from select-all. Default (absent):
   * every row is selectable. Only meaningful alongside {@link selectedIds}.
   */
  isRowSelectable?: (row: T) => boolean;
  /**
   * Opt-in search box rendered above the table. Filters rows client-side
   * over every column's rendered text (or via {@link searchPredicate} when
   * provided). Host pages own no state — the input is uncontrolled.
   */
  searchable?: boolean;
  searchPlaceholder?: string;
  searchPredicate?: (row: T, query: string) => boolean;
  /**
   * Opt-in column sorting. Columns must declare {@link Column.sortable} and
   * {@link Column.sortAccessor} to participate. Header click toggles
   * asc → desc → off.
   */
  sortable?: boolean;
  /** Default sort applied on mount (resettable by clicking the column header). */
  defaultSort?: { key: string; dir: "asc" | "desc" };
  /**
   * Opt-in pagination. Pass <code>"auto"</code> for the 25-row default.
   * Render order: filter → sort → group → paginate.
   */
  pagination?: { pageSize: number } | "auto";
  /**
   * Opt-in grouping. Rows sharing a group key render under a collapsible
   * header. When a function, the returned string is the group label;
   * when a key, the row's value at that key is used (coerced to string).
   */
  groupBy?: keyof T | ((row: T) => string);
  /** Render label for the group header. Defaults to the group key. */
  groupLabel?: (groupKey: string, rowsInGroup: T[]) => ReactNode;
  /**
   * Optional actions rendered on the right side of a group header row
   * (e.g. per-group CTAs), justified opposite the collapse toggle. Rendered
   * outside the toggle <button> so its own links/buttons stay interactive.
   */
  groupActions?: (groupKey: string, rowsInGroup: T[]) => ReactNode;
  /**
   * Optional extra class applied to each body row. Opt-in per call site
   * (e.g. <code>[&>td]:py-space-4</code> for roomier rows); other tables
   * keep the default density. Pass a function to vary the class per row
   * (e.g. a subtle urgency tint); existing string call sites are unaffected.
   */
  rowClassName?: string | ((row: T) => string | undefined);
  /**
   * Optional callback receiving each rendered row's <tr> element. Opt-in;
   * lets a host page scroll to / flash a specific row (e.g. a deep-linked
   * submission). Other call sites are unaffected.
   */
  rowRef?: (row: T, el: HTMLTableRowElement | null) => void;
  /**
   * Opt-in whole-row click. When set, each body row becomes clickable
   * (pointer cursor + hover affordance) and fires this with the row.
   * Interactive children (buttons / links) should stopPropagation so they
   * win over the row handler. Other call sites are unaffected.
   */
  onRowClick?: (row: T) => void;
  /** Opt-in sticky header (kit Table passthrough). */
  stickyHeader?: boolean;
  /** Optional per-row data-testid for host-page tests. */
  rowTestId?: (row: T) => string;
  /** data-testid applied to the table element. */
  "data-testid"?: string;
}

const DEFAULT_PAGE_SIZE = 25;

function defaultSearchPredicate<T>(columns: Column<T>[], row: T, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return columns.some((col) => {
    const node = col.render(row);
    const text = nodeToText(node).toLowerCase();
    return text.includes(q);
  });
}

function nodeToText(node: ReactNode): string {
  if (node == null || node === false || node === true) return "";
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(nodeToText).join(" ");
  if (typeof node === "object" && "props" in node) {
    return nodeToText((node as { props: { children?: ReactNode } }).props.children);
  }
  return "";
}

function compareValues(a: unknown, b: unknown): number {
  if (a == null && b == null) return 0;
  if (a == null) return -1;
  if (b == null) return 1;
  if (a instanceof Date && b instanceof Date) return a.getTime() - b.getTime();
  if (typeof a === "number" && typeof b === "number") return a - b;
  return String(a).localeCompare(String(b));
}

export function DataTable<T>({
  columns,
  rows,
  loading,
  error,
  emptyMessage,
  rowKey,
  selectedIds,
  onSelectionChange,
  isRowSelectable,
  searchable,
  searchPlaceholder,
  searchPredicate,
  sortable,
  defaultSort,
  pagination,
  groupBy,
  groupLabel,
  groupActions,
  rowClassName,
  rowRef,
  onRowClick,
  stickyHeader,
  rowTestId,
  "data-testid": testId,
}: DataTableProps<T>) {
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState<SortState>(defaultSort ?? null);
  const [page, setPage] = useState(0);
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());

  const pageSize =
    pagination === "auto"
      ? DEFAULT_PAGE_SIZE
      : pagination && typeof pagination === "object"
        ? pagination.pageSize
        : null;

  // filter → sort → group → paginate
  const filtered = useMemo(() => {
    if (!searchable || !query.trim()) return rows;
    const predicate = searchPredicate ?? ((row: T, q: string) => defaultSearchPredicate(columns, row, q));
    return rows.filter((r) => predicate(r, query));
  }, [rows, searchable, query, searchPredicate, columns]);

  const sorted = useMemo(() => {
    if (!sortable || !sort) return filtered;
    const col = columns.find((c) => c.key === sort.key && c.sortable && c.sortAccessor);
    if (!col || !col.sortAccessor) return filtered;
    const factor = sort.dir === "asc" ? 1 : -1;
    return [...filtered].sort((a, b) => compareValues(col.sortAccessor!(a), col.sortAccessor!(b)) * factor);
  }, [filtered, sortable, sort, columns]);

  const groups = useMemo<{ key: string; rows: T[] }[]>(() => {
    if (!groupBy) return [{ key: "__all", rows: sorted }];
    const map = new Map<string, T[]>();
    for (const row of sorted) {
      const key =
        typeof groupBy === "function"
          ? groupBy(row)
          : String(row[groupBy] ?? "");
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(row);
    }
    return [...map.entries()].map(([key, rs]) => ({ key, rows: rs }));
  }, [sorted, groupBy]);

  const paged = useMemo(() => {
    if (!pageSize) return groups;
    // Paginate across the flattened sequence so groups span pages naturally.
    const flat: { key: string; rows: T[] }[] = groups.map((g) => ({ key: g.key, rows: [] }));
    let skipped = 0;
    let taken = 0;
    const start = page * pageSize;
    const end = start + pageSize;
    for (let gi = 0; gi < groups.length; gi++) {
      for (const row of groups[gi].rows) {
        const idx = skipped++;
        if (idx >= end) break;
        if (idx < start) continue;
        flat[gi].rows.push(row);
        taken++;
        if (taken >= pageSize) break;
      }
      if (taken >= pageSize) break;
    }
    return flat.filter((g) => g.rows.length > 0);
  }, [groups, page, pageSize]);

  const totalRowCount = sorted.length;
  const totalPages = pageSize ? Math.max(1, Math.ceil(totalRowCount / pageSize)) : 1;
  const currentPage = pageSize ? Math.min(page, totalPages - 1) : 0;

  // Reset page when the filter query changes.
  useEffect(() => {
    setPage(0);
  }, [query]);

  const selectable = !!selectedIds && !!onSelectionChange;
  const visibleRows = paged.flatMap((g) => g.rows);
  const selectedSet = selectable ? new Set(selectedIds) : null;
  const canSelectRow = (row: T) => !isRowSelectable || isRowSelectable(row);
  // Select-all and the all/some header state consider only the rows the host
  // allows selecting. With no predicate this is every visible row — unchanged.
  const selectableOnPageIds = selectable ? visibleRows.filter(canSelectRow).map(rowKey) : [];
  const allSelected =
    selectable && selectableOnPageIds.length > 0 && selectableOnPageIds.every((id) => selectedSet!.has(id));
  const someSelected =
    selectable && !allSelected && selectableOnPageIds.some((id) => selectedSet!.has(id));

  const toggleAll = () => {
    if (!selectable) return;
    if (allSelected) {
      onSelectionChange!(selectedIds!.filter((id) => !selectableOnPageIds.includes(id)));
    } else {
      const next = new Set(selectedIds);
      selectableOnPageIds.forEach((id) => next.add(id));
      onSelectionChange!([...next]);
    }
  };

  const toggleRow = (id: string) => {
    if (!selectable) return;
    if (selectedSet!.has(id)) {
      onSelectionChange!(selectedIds!.filter((x) => x !== id));
    } else {
      onSelectionChange!([...selectedIds!, id]);
    }
  };

  const toggleSort = (col: Column<T>) => {
    if (!sortable || !col.sortable || !col.sortAccessor) return;
    setSort((prev) => {
      if (!prev || prev.key !== col.key) return { key: col.key, dir: "asc" };
      if (prev.dir === "asc") return { key: col.key, dir: "desc" };
      return null;
    });
  };

  const toggleGroup = (key: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  if (loading) {
    return (
      <div className="py-space-6 text-center text-caption uppercase tracking-wide text-gray-mid">
        Loading…
      </div>
    );
  }
  if (error) {
    return (
      <div className="py-space-5 text-center text-small text-status-error">
        {error}
      </div>
    );
  }

  const showSearchBar = searchable;
  const showPagination = pageSize !== null && totalRowCount > pageSize;
  const colSpan = columns.length + (selectable ? 1 : 0);

  return (
    <div className="flex flex-col gap-space-3">
      {showSearchBar && (
        <div className="relative max-w-md">
          <Search
            size={14}
            strokeWidth={1.5}
            aria-hidden
            className="pointer-events-none absolute left-space-3 top-1/2 -translate-y-1/2 text-gray-mid"
          />
          <Input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={searchPlaceholder ?? "Search…"}
            aria-label={searchPlaceholder ?? "Search"}
            className="pl-space-7"
            data-testid="datatable-search"
          />
        </div>
      )}

      {visibleRows.length === 0 ? (
        <div className="py-space-6 text-center text-small text-gray-mid">
          {emptyMessage ?? "No records to display."}
        </div>
      ) : (
        <Table stickyHeader={stickyHeader} data-testid={testId}>
          <THead>
            <TR>
              {selectable && (
                <TH className="w-10">
                  <input
                    type="checkbox"
                    aria-label="Select all on page"
                    className="h-4 w-4 accent-[var(--gold)]"
                    checked={allSelected}
                    ref={(el) => {
                      if (el) el.indeterminate = someSelected;
                    }}
                    onChange={toggleAll}
                  />
                </TH>
              )}
              {columns.map((c) => {
                const isSortable = sortable && c.sortable && !!c.sortAccessor;
                const isActive = sort?.key === c.key;
                return (
                  <TH
                    key={c.key}
                    className={cn(c.className, c.mobileHidden && "hidden sm:table-cell")}
                  >
                    {isSortable ? (
                      <button
                        type="button"
                        onClick={() => toggleSort(c)}
                        className="inline-flex items-center gap-1 hover:text-white-pure focus:outline-none focus-visible:underline"
                        data-testid={`datatable-sort-${c.key}`}
                      >
                        {c.header}
                        {isActive && sort?.dir === "asc" ? (
                          <ChevronUp size={12} strokeWidth={2} aria-hidden />
                        ) : isActive && sort?.dir === "desc" ? (
                          <ChevronDown size={12} strokeWidth={2} aria-hidden />
                        ) : (
                          <ChevronsUpDown size={12} strokeWidth={2} aria-hidden className="opacity-50" />
                        )}
                      </button>
                    ) : (
                      c.header
                    )}
                  </TH>
                );
              })}
            </TR>
          </THead>
          <TBody>
            {paged.map((group) => {
              const isGrouped = !!groupBy;
              const collapsed = isGrouped && collapsedGroups.has(group.key);
              return (
                <Fragment key={`__g_${group.key}`}>
                  {isGrouped && (
                    <TR className="bg-onyx/5 hover:bg-onyx/10">
                      <TD colSpan={colSpan} className="py-space-2">
                        <div className="flex items-center justify-between gap-space-3">
                          <button
                            type="button"
                            onClick={() => toggleGroup(group.key)}
                            className="inline-flex items-center gap-2 font-display text-label font-semibold uppercase tracking-label text-onyx"
                            aria-expanded={!collapsed}
                            data-testid={`datatable-group-${group.key}`}
                          >
                            {collapsed ? (
                              <ChevronRight size={14} strokeWidth={2} aria-hidden />
                            ) : (
                              <ChevronDown size={14} strokeWidth={2} aria-hidden />
                            )}
                            <span>
                              {groupLabel ? groupLabel(group.key, group.rows) : group.key || "—"}
                            </span>
                            <span className="text-gray-mid">({group.rows.length})</span>
                          </button>
                          {groupActions && groupActions(group.key, group.rows)}
                        </div>
                      </TD>
                    </TR>
                  )}
                  {!collapsed &&
                    group.rows.map((row) => {
                      const id = rowKey(row);
                      const checked = selectable && selectedSet!.has(id);
                      const rowCls =
                        typeof rowClassName === "function" ? rowClassName(row) : rowClassName;
                      return (
                        <TR
                          key={id}
                          className={cn(
                            rowCls,
                            checked && "bg-gold-highlight/10",
                            onRowClick && "cursor-pointer hover:bg-white-warm",
                          )}
                          ref={rowRef ? (el) => rowRef(row, el) : undefined}
                          onClick={onRowClick ? () => onRowClick(row) : undefined}
                          data-testid={rowTestId ? rowTestId(row) : undefined}
                        >
                          {selectable && (
                            <TD className="w-10">
                              <input
                                type="checkbox"
                                aria-label={`Select row ${id}`}
                                className="h-4 w-4 accent-[var(--gold)] disabled:opacity-40 disabled:cursor-not-allowed"
                                checked={checked}
                                disabled={!canSelectRow(row)}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  toggleRow(id);
                                }}
                                onClick={(e) => e.stopPropagation()}
                              />
                            </TD>
                          )}
                          {columns.map((c) => (
                            <TD
                              key={c.key}
                              className={cn(c.className, c.mobileHidden && "hidden sm:table-cell")}
                            >
                              {c.render(row)}
                            </TD>
                          ))}
                        </TR>
                      );
                    })}
                </Fragment>
              );
            })}
          </TBody>
        </Table>
      )}

      {showPagination && (
        <div className="flex items-center justify-between text-caption text-gray-mid">
          <div>
            Showing {currentPage * pageSize! + 1}–
            {Math.min((currentPage + 1) * pageSize!, totalRowCount)} of {totalRowCount}
          </div>
          <div className="flex items-center gap-space-2">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={currentPage === 0}
              className="px-space-2 py-1 border border-border-light rounded-md disabled:opacity-40"
              data-testid="datatable-prev"
            >
              Prev
            </button>
            <span>
              Page {currentPage + 1} of {totalPages}
            </span>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={currentPage >= totalPages - 1}
              className="px-space-2 py-1 border border-border-light rounded-md disabled:opacity-40"
              data-testid="datatable-next"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
